/**
 * BankAccountsDynamicTab — F12.N T5.5min.
 *
 * Zakładka Konta bankowe kontrahenta — wrapper wokół `BankAccountsTabBase`
 * (wspólna implementacja z `CompanyBankAccountsTab`, patrz jej JSDoc dla
 * pełnego opisu). Ten plik niesie tylko różnice wariantu kontrahenta:
 * backend `/api/contractors/:id/bank-accounts` (unwrapped array), moduł
 * helper-encji `contractor_bank_accounts`, i whitelist VAT MF (kolumna status +
 * akcja „Zweryfikuj" + przycisk „Synchronizuj z białą listą" w nagłówku,
 * gated `taxId`).
 *
 * Public API: `{ contractorId, taxId, testIdPrefix? }`. Wymiana w `ContractorDetail.tsx`.
 */

import { useMemo } from 'react';

import type { BankAccountRow } from './bank-accounts-columns';
import { BankAccountsTabBase, type BankAccountsTabConfig } from './BankAccountsTabBase';

export interface BankAccountsDynamicTabProps {
  contractorId: string;
  /** NIP kontrahenta (surowy, może zawierać myślniki/spacje) — steruje dostępnością
   *  przycisku „Synchronizuj z białą listą" (wymaga dokładnie 10 cyfr po oczyszczeniu). */
  taxId: string | null;
  /** Custom testid prefix (default: `bank-accounts`). */
  testIdPrefix?: string;
  /** Slugi kolumn ukrytych z layoutu (formatka bloku `contractor_bank_accounts_view`). */
  hiddenColumnIds?: readonly string[];
  /** Kolejność kolumn (slugi) z formatki bloku — drag. */
  columnOrder?: readonly string[];
}

export function BankAccountsDynamicTab({
  contractorId,
  taxId,
  testIdPrefix = 'bank-accounts',
  hiddenColumnIds,
  columnOrder,
}: BankAccountsDynamicTabProps) {
  const config = useMemo<BankAccountsTabConfig>(
    () => ({
      moduleSlug: 'contractor_bank_accounts',
      apiBase: `/contractors/${contractorId}/bank-accounts`,
      queryKey: ['contractor-bank-accounts', contractorId],
      // Backend zwraca array bezpośrednio (nie `{ bankAccounts: [...] }`).
      unwrapList: (data) => data as BankAccountRow[],
      whitelist: { taxId, contractorId },
      ...(hiddenColumnIds ? { hiddenColumnIds } : {}),
      ...(columnOrder ? { columnOrder } : {}),
    }),
    [contractorId, taxId, hiddenColumnIds, columnOrder],
  );

  return <BankAccountsTabBase config={config} testIdPrefix={testIdPrefix} />;
}
